import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import Home from './components/screens/Home';
import LandingPage from './components/screens/LandingPage';
import Login from './components/screens/Login';
import Register from './components/screens/Register';
import AuthCallback from './components/screens/AuthCallback';
import AuthConfirm from './components/screens/AuthConfirm';
import EmailVerification from './components/screens/EmailVerification';
import AddShow from './components/screens/AddShow';
import EditShow from './components/screens/EditShow';
import Moderation from './components/screens/Moderation';
import MyShows from './components/screens/MyShows';
import Settings from './components/screens/Settings';
import FAQ from './components/screens/FAQ';
import AdminAnalytics from './components/admin/AdminAnalytics';
import ErrorBoundary from './components/ErrorBoundary';
import InstallPrompt from './components/widgets/InstallPrompt';
import LoadingSpinner from './components/widgets/LoadingSpinner';
import { ThemeProvider as CustomThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ConnectionStatus from './components/widgets/ConnectionStatus';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text.primary};
    min-height: 100vh;
  }

  html, body, #root {
    height: 100%;
  }

  button, input, textarea, select {
    font-family: inherit;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.surface};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.secondary};
  }

  /* PWA specific styles */
  @media all and (display-mode: standalone) {
    body {
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
    }
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner text="Loading..." subtext="Tweaking Levels!" />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/landing" replace />;
  }
  
  return children;
};

const AppContent = () => {
  const { theme } = useTheme();
  const { isAuthenticated, loading } = useAuth();
  
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AppContainer>
        <ErrorBoundary>
          <ConnectionStatus />
          <Router>
            <Routes>
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/" element={
                loading ? (
                  <LoadingSpinner text="Loading..." subtext="Tweaking Levels!" />
                ) : isAuthenticated ? (
                  <Home />
                ) : (
                  <Navigate to="/landing" replace />
                )
              } />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/confirm" element={<AuthConfirm />} />
              <Route path="/auth/verify-email" element={<EmailVerification />} />
              <Route path="/add-show" element={
                <ProtectedRoute>
                  <AddShow />
                </ProtectedRoute>
              } />
              <Route path="/edit-show/:showId" element={
                <ProtectedRoute>
                  <EditShow />
                </ProtectedRoute>
              } />
              <Route path="/moderation" element={
                <ProtectedRoute>
                  <Moderation />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/my-shows" element={
                <ProtectedRoute>
                  <MyShows />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/faq" element={<FAQ />} />
            </Routes>
            <InstallPrompt />
          </Router>
        </ErrorBoundary>
        <Toaster
          position="bottom-center"
          toastOptions={{
            duration: 6000,
            style: {
              background: theme.colors.surface,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border}`,
              marginBottom: '80px', // Move up from bottom to avoid mobile browser UI
              zIndex: 9999,
            },
            success: {
              iconTheme: {
                primary: theme.colors.success,
                secondary: theme.colors.surface,
              },
            },
            error: {
              iconTheme: {
                primary: theme.colors.error,
                secondary: theme.colors.surface,
              },
            },
          }}
        />
      </AppContainer>
    </ThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <CustomThemeProvider>
        <AppContent />
      </CustomThemeProvider>
    </AuthProvider>
  );
}

export default App; 